import { Box, Typography, TextField, Select, MenuItem, IconButton, FormControl } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { typeSets } from "../../../enums/TypeSets";
import { RTL } from "../../../cache/CacheRtl";
import { nonNegative, nonNegativeInputProps } from "../../../utils/numberInput";

const MobileIntervalSection = ({
  exercise,
  setWorkout,
  editMode,
  prvExercise,
  blockIdx,
  weekIdx,
  exerciseRange,
  onRangeChange
}) => {
  const isFirstBlockAndWeek = blockIdx === 0 && weekIdx === 0;

  const addInterval = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      let intervals = programExercises.at(exercise.index).intervals || [];

      const sorted = [...intervals].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const last = sorted.length > 0 ? sorted[sorted.length - 1] : null;
      const minReps = last?.minReps ?? null;
      const maxReps = last?.maxReps ?? null;
      const recommendedReps = (minReps != null && maxReps != null) ? Math.round((minReps + maxReps) / 2) : 10;
      intervals.push({
        index: intervals.length,
        recommendedReps,
        typeSet: isFirstBlockAndWeek ? "RM_SET" : "REGULAR_SET",
        minReps,
        maxReps
      });

      programExercises.at(exercise.index).intervals = intervals;
      newWorkout.programExercises = programExercises;
      return newWorkout;
    });
  };

  const deleteInterval = (intervalIndex) => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      const programExercise = programExercises.at(exercise.index);

      let idx = 0;
      programExercise.intervals = programExercise.intervals
        .sort((a, b) => a.index > b.index ? 1 : -1)
        .filter(x => x.index !== intervalIndex)
        .map(val => {
          val.index = idx;
          idx += 1;
          return val;
        });

      return newWorkout;
    });
  };

  const updateIntervalField = (intervalIndex, field, value) => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      const programExercise = programExercises.at(exercise.index);
      programExercise.intervals.find(i => i.index === intervalIndex)[field] = value;
      return newWorkout;
    });
  };

  const intervals = exercise.intervals?.sort((a, b) => a.index > b.index ? 1 : -1) || [];

  return (
    <Box sx={{ padding: "12px 14px", backgroundColor: "#FAFBFC" }}>
      {intervals.map((interval, idx) => {
        const prvInterval = prvExercise?.intervals?.find(i => i.index === interval.index);
        const intervalRange = exerciseRange?.intervals?.find(i => i.index === interval.index);
        const effectiveMinReps = intervalRange?.minReps ?? exerciseRange?.minReps ?? null;
        const effectiveMaxReps = intervalRange?.maxReps ?? exerciseRange?.maxReps ?? null;
        const hasRange = effectiveMinReps != null || effectiveMaxReps != null;

        return (
          <Box
            key={interval.id || idx}
            sx={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px",
              marginBottom: "10px"
            }}
          >
            {/* Set Header */}
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px"
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
                  סט {interval.index + 1}
                </Typography>
                {editMode ? (
                  <Box sx={{
                    display: "flex",
                    direction: "ltr",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "#F5F3FF",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid #DDD6FE"
                  }}>
                    <TextField
                      value={effectiveMinReps ?? ''}
                      placeholder={exerciseRange?.minReps?.toString() || '-'}
                      onChange={(e) => onRangeChange && onRangeChange(interval.index, 'minReps', parseIntOrNull(e.target.value))}
                      type="number"
                      size="small"
                      variant="standard"
                      inputProps={{
                        ...nonNegativeInputProps,
                        style: { fontSize: "11px", textAlign: "center", padding: 0, width: "25px", color: "#8B5CF6" }
                      }}
                      sx={{ "& .MuiInput-underline:before": { borderBottom: "none" } }}
                    />
                    <Typography sx={{ fontSize: "11px", color: "#8B5CF6" }}>-</Typography>
                    <TextField
                      value={effectiveMaxReps ?? ''}
                      placeholder={exerciseRange?.maxReps?.toString() || '-'}
                      onChange={(e) => onRangeChange && onRangeChange(interval.index, 'maxReps', parseIntOrNull(e.target.value))}
                      type="number"
                      size="small"
                      variant="standard"
                      inputProps={{
                        ...nonNegativeInputProps,
                        style: { fontSize: "11px", textAlign: "center", padding: 0, width: "25px", color: "#8B5CF6" }
                      }}
                      sx={{ "& .MuiInput-underline:before": { borderBottom: "none" } }}
                    />
                  </Box>
                ) : (
                  hasRange && (
                    <Typography sx={{
                      fontSize: "11px",
                      color: "#8B5CF6",
                      backgroundColor: "#F5F3FF",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontWeight: 500,
                      border: "1px solid #DDD6FE"
                    }}>
                      {effectiveMinReps || '?'}-{effectiveMaxReps || '?'}
                    </Typography>
                  )
                )}
              </Box>
              {editMode && (
                <IconButton
                  size="small"
                  onClick={() => deleteInterval(interval.index)}
                  sx={{ padding: "4px" }}
                >
                  <Delete sx={{ fontSize: "18px", color: "#EF4444" }} />
                </IconButton>
              )}
            </Box>

            {/* Interval Data Grid */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px"
            }}>
              {/* Recommended Reps */}
              <DataCell
                label="חזרות מומלצות"
                value={interval.recommendedReps}
                prevValue={prvInterval?.recommendedReps}
                editable={editMode}
                onChange={(val) => updateIntervalField(interval.index, 'recommendedReps', val)}
                type="number"
                nullText="מקסימום חזרות"
              />

              {/* Actual Reps */}
              <DataCell
                label="חזרות שבוצעו"
                value={interval.reps}
                prevValue={prvInterval?.reps}
                editable={editMode}
                onChange={(val) => updateIntervalField(interval.index, 'reps', val)}
                type="number"
              />

              {/* Set Type */}
              <Box sx={{
                backgroundColor: "#F8FAFC",
                borderRadius: "6px",
                padding: "6px",
                textAlign: "center"
              }}>
                <Typography sx={{ fontSize: "9px", color: "#94A3B8", mb: 0.5 }}>סוג סט</Typography>
                {editMode ? (
                  <RTL>
                    <FormControl fullWidth size="small">
                      <Select
                        value={interval.typeSet || ""}
                        onChange={(e) => updateIntervalField(interval.index, 'typeSet', e.target.value)}
                        sx={{
                          fontSize: "11px",
                          "& .MuiSelect-select": { padding: "4px 6px" }
                        }}
                      >
                        {typeSets.map((type, i) => (
                          <MenuItem key={i} value={type} sx={{ fontSize: "11px" }}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </RTL>
                ) : (
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#1E293B" }}>
                    {interval.typeSet || "-"}
                  </Typography>
                )}
              </Box>

              {/* Recommended Weight */}
              <DataCell
                label="משקל מומלץ"
                value={interval.recommendedWeight}
                prevValue={prvInterval?.recommendedWeight}
                editable={editMode}
                onChange={(val) => updateIntervalField(interval.index, 'recommendedWeight', val)}
                type="number"
                nullText="מקסימום משקל"
              />

              {/* Actual Weight */}
              <DataCell
                label="משקל שבוצע"
                value={interval.weight}
                prevValue={prvInterval?.weight}
                editable={editMode}
                onChange={(val) => updateIntervalField(interval.index, 'weight', val)}
                type="number"
              />

              {/* RIR */}
              <DataCell
                label="RIR"
                value={interval.rir}
                prevValue={prvInterval?.rir}
                editable={editMode}
                onChange={(val) => updateIntervalField(interval.index, 'rir', val)}
                type="number"
              />
            </Box>
          </Box>
        );
      })}

      {/* Add Set Button */}
      {editMode && (
        <Box
          onClick={addInterval}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px",
            backgroundColor: "#FFFFFF",
            border: "1px dashed #CBD5E1",
            borderRadius: "10px",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: "#F8FAFC",
              borderColor: "#94A3B8"
            }
          }}
        >
          <Add sx={{ color: "#64748B", fontSize: "18px", mr: 0.5 }} />
          <Typography sx={{ fontSize: "13px", color: "#64748B", fontWeight: 500 }}>
            הוסף סט
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {intervals.length === 0 && !editMode && (
        <Box sx={{
          textAlign: "center",
          padding: "20px",
          color: "#94A3B8"
        }}>
          <Typography sx={{ fontSize: "13px" }}>אין סטים</Typography>
        </Box>
      )}
    </Box>
  );
};

/** Reps ranges hold a number, not the raw string; a pasted negative becomes null. */
const parseIntOrNull = (raw) => {
  const value = nonNegative(raw);
  return value ? parseInt(value) : null;
};

// Helper component for data cells
const DataCell = ({ label, value, prevValue, editable, onChange, type, nullText }) => (
  <Box sx={{
    backgroundColor: "#F8FAFC",
    borderRadius: "6px",
    padding: "6px",
    textAlign: "center"
  }}>
    <Typography sx={{ fontSize: "9px", color: "#94A3B8", mb: 0.5 }}>{label}</Typography>
    {editable ? (
      <TextField
        value={value || ""}
        onChange={(e) => onChange(type === "number" ? nonNegative(e.target.value) : e.target.value)}
        type={type}
        size="small"
        variant="standard"
        inputProps={{
          ...(type === "number" ? nonNegativeInputProps : {}),
          style: {
            fontSize: "12px",
            textAlign: "center",
            padding: 0
          }
        }}
        sx={{
          width: "100%",
          "& .MuiInput-underline:before": { borderBottom: "none" },
          "& .MuiInput-underline:after": { borderBottom: "1px solid #3B82F6" }
        }}
      />
    ) : (
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
          {value == null ? (nullText || "-") : value}
        </Typography>
        {prevValue != null && prevValue !== value && (
          <Typography sx={{ fontSize: "9px", color: "#94A3B8" }}>
            ({prevValue})
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

export default MobileIntervalSection;
